import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { Loader2, QrCode, Smartphone } from 'lucide-react';
import { Toaster } from '@/components/ui/sonner';
import { AppShell } from '@/components/layout/AppShell';
import { ErrorState, StateView } from '@/components/common/StateView';
import { GuestSessionProvider, useGuest } from '@/contexts/GuestSessionContext';
import { RequestsProvider } from '@/contexts/RequestsContext';
import { HomePage } from '@/pages/HomePage';
import { CallPage } from '@/features/call/CallPage';
import { HousekeepingPage } from '@/features/housekeeping/HousekeepingPage';
import { RestaurantPage, StorePage } from '@/features/restaurant/RestaurantPage';
import { LaundryPage } from '@/features/laundry/LaundryPage';
import { FeedbackPage } from '@/features/feedback/FeedbackPage';
import { MyRequestsPage } from '@/features/requests/MyRequestsPage';
import { WifiPage } from '@/features/wifi/WifiPage';
import { PlacesToExplorePage } from '@/features/explore/PlacesToExplorePage';
import { PhoneGateDialog } from '@/features/session/PhoneGateDialog';

/**
 * Session gate, then the router.
 *
 * Nothing routable is mounted until the guest session is `ready`, so every
 * page can assume a hotel, a room and a uid (`useReadyGuest`). The location
 * survives navigation because `readGuestLocation` keeps it in sessionStorage;
 * the `?hotelId=&roomId=` query string is only needed on the first load.
 *
 * Between a valid room and `ready` sits the phone check: the server answers
 * the first registration with `GUEST_PHONE_REQUIRED`, the popup collects the
 * number, and a mismatch is shown inside the popup so the guest can try
 * again. No page renders behind the popup — there is no session yet.
 */
function Gate() {
  const { phase, errorMessage, retry, submitPhone } = useGuest();

  if (phase === 'loading') {
    return (
      <AppShell showNav={false}>
        <div className="flex flex-1 flex-col items-center justify-center gap-3" role="status">
          <Loader2 className="text-primary size-8 animate-spin" aria-hidden="true" />
          <p className="text-muted-foreground text-sm">Connecting…</p>
        </div>
      </AppShell>
    );
  }

  if (phase === 'missing-location') {
    return (
      <AppShell showNav={false}>
        <StateView
          icon={QrCode}
          title="Scan the code in your room"
          message="Please scan the QR code in your room to reach reception and hotel services."
        />
      </AppShell>
    );
  }

  if (phase === 'phone-required') {
    return (
      <AppShell showNav={false}>
        <div className="flex flex-1 flex-col items-center justify-center gap-3" role="status">
          <Smartphone className="text-muted-foreground size-8" aria-hidden="true" />
          <p className="text-muted-foreground text-sm">Verifying your stay…</p>
        </div>
        <PhoneGateDialog onSubmit={submitPhone} />
      </AppShell>
    );
  }

  if (phase === 'error') {
    return (
      <AppShell showNav={false}>
        <ErrorState
          title="Couldn’t connect"
          message={errorMessage ?? "We couldn't connect to reception. Please try again."}
          onRetry={retry}
        />
      </AppShell>
    );
  }

  // Requests are subscribed above the routes so a status change from the
  // desk raises a toast on whichever page the guest is looking at.
  return (
    <RequestsProvider>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/call" element={<CallPage />} />
        <Route path="/housekeeping" element={<HousekeepingPage />} />
        <Route path="/restaurant" element={<RestaurantPage />} />
        <Route path="/store" element={<StorePage />} />
        <Route path="/laundry" element={<LaundryPage />} />
        <Route path="/feedback" element={<FeedbackPage />} />
        <Route path="/wifi" element={<WifiPage />} />
        <Route path="/explore" element={<PlacesToExplorePage />} />
        <Route path="/requests" element={<MyRequestsPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </RequestsProvider>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <GuestSessionProvider>
        <Gate />
        <Toaster />
      </GuestSessionProvider>
    </BrowserRouter>
  );
}
